import { Filesystem, Directory } from '@capacitor/filesystem'
import { decodedByteLength } from '../base64'
import { dbg, dbgWarn } from '../../logger'

/**
 * NativeFsBackend — backend de binarios para móvil nativo vía @capacitor/filesystem.
 *
 * El binario vive como archivo bajo Directory.Data (sandbox privado persistente) con ruta
 * RELATIVA `offline_images/<image_ref>.jpeg`. SQLite SOLO guarda esa ruta relativa — nunca una
 * uri absoluta (bug de reinstalación documentado en v1).
 *
 * Contrato (idéntico al backend web): write(ref,base64DataUrl)->ruta relativa /
 * read(record)->base64 crudo | null / del(record)->void idempotente.
 *
 * write es ATÓMICO (integridad v9): tmp + verify (tamaño vs photo_meta.byte_length esperado) +
 * rename — el nombre final solo existe con el contenido completo y verificado.
 */

const IMAGES_FOLDER = 'offline_images'
const FILE_NOT_FOUND = 'OS-PLUG-FILE-0008'

const relPathFor = (imageRef) => `${IMAGES_FOLDER}/${imageRef}.jpeg`

/** Cuerpo base64 crudo (sin prefijo data:) — Filesystem.writeFile lo escribe directo como binario. */
function stripDataUrlPrefix (base64DataUrl) {
  const comma = base64DataUrl.indexOf(',')
  return base64DataUrl.startsWith('data:') && comma > -1 ? base64DataUrl.substring(comma + 1) : base64DataUrl
}

export const NativeFsBackend = {
  /**
   * @param {string} imageRef
   * @param {string} base64DataUrl
   * @returns {Promise<string>} ruta relativa local_path.
   */
  async write (imageRef, base64DataUrl) {
    // Un binario vacío/no decodificable jamás llega al disco con nombre final (el throw cae en
    // reportPhotoCaptureFailed vía capture).
    const expectedBytes = decodedByteLength(base64DataUrl)
    if (!expectedBytes) throw new Error('[offline-v2] FS write: binario vacío/no decodificable')
    // ESCRITURA ATÓMICA (integridad v9): tmp + verify + rename. Escribir DIRECTO al path final deja,
    // ante un kill/reinicio a media escritura, un archivo TRUNCADO con nombre válido que después se
    // pinta BLANCO y hasta se sube al server. El nombre final solo existe con el contenido completo y
    // verificado. (Sin fsync no hay garantía absoluta ante apagón duro — por eso la RED de seguridad
    // real es photo_meta.byte_length en los resolvers; esto estrecha la ventana.)
    const path = relPathFor(imageRef)
    const tmpPath = `${path}.tmp`
    // writeFile reemplaza el contenido completo -> un tmp huérfano de un intento previo no deja cola stale.
    await Filesystem.writeFile({ path: tmpPath, data: stripDataUrlPrefix(base64DataUrl), directory: Directory.Data, recursive: true })
    // VERIFY: releer el tamaño real en disco contra los bytes decodificados esperados
    // (contrato decodedByteLength(x) === base64ToBlob(x).size, ver base64.js).
    const { size } = await Filesystem.stat({ path: tmpPath, directory: Directory.Data })
    if (size !== expectedBytes) {
      try { await Filesystem.deleteFile({ path: tmpPath, directory: Directory.Data }) } catch { /* best-effort */ }
      throw new Error(`[offline-v2] FS write corto: ${size}/${expectedBytes} bytes`)
    }
    // COMMIT: rename al nombre final (mismo volumen -> move atómico). El final NUNCA existe de antes
    // (imageRef = newId() fresco) -> rename limpio sin semántica de overwrite.
    await Filesystem.rename({ from: tmpPath, to: path, directory: Directory.Data, toDirectory: Directory.Data })
    dbg('photo', `FS write OK -> ${path} (${size} bytes, tmp+verify+rename)`)
    return path
  },

  /**
   * @param {{ local_path: string }} record
   * @returns {Promise<string|null>} dataURL completo, uniforme con WebIdbBackend. Nunca lanza.
   */
  async read (record) {
    if (!record || !record.local_path) return null
    try {
      const { data } = await Filesystem.readFile({ path: record.local_path, directory: Directory.Data })
      return `data:image/jpeg;base64,${data}`
    } catch (e) {
      dbgWarn('photo', `FS read FALLÓ para ${record.local_path} (¿binario movido/borrado?)`, e)
      return null
    }
  },

  /**
   * @param {{ local_path: string }} record
   * @returns {Promise<void>} idempotente.
   */
  async del (record) {
    if (!record || !record.local_path) return
    try {
      await Filesystem.deleteFile({ path: record.local_path, directory: Directory.Data })
    } catch (e) {
      if (e && e.code !== FILE_NOT_FOUND) dbgWarn('photo', 'FS del (ignorado: idempotente)', e)
    }
  }
}
